/**
 * NodeCommentPopup - Popover for viewing and adding threaded comments on a metric node.
 *
 * Users with COMMENT permission can add comments and reply.
 * Users with READ permission can view comments.
 * Mentions go through useMentionShareFlow to ensure mentioned users have access.
 */
import { useState, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'src/components/ui/toast';
import {
  XIcon,
  PaperPlaneTiltIcon,
  PencilSimpleIcon,
  TrashIcon,
  ArrowBendDownRightIcon,
  InfoIcon
} from '@phosphor-icons/react';
import { apiGet } from '@/lib/api/axios';
import { formatRelativeTime } from '@/lib/utils/formatRelativeTime';
import { Textarea } from '@/components/ui/Textarea';
import { renderContentWithMentions } from 'src/lib/mentions';
import Button from 'src/components/ui/Button';
import Loading from 'src/components/ui/Loading';
import Popover from 'src/components/ui/Popover';
import ShareModal from 'src/components/ui/ShareModal';
import useAuth from 'src/hooks/useAuth';
import { MentionData, addMentionMarkers } from 'src/hooks/useMentionUsers';
import { useMentionShareFlow } from 'src/hooks/useMentionShareFlow';
import { ResourcePermissionRole } from 'src/features/chat/types';
import { FISCAL_QUARTERS, MONTHS, MONTH_TO_FISCAL_QUARTER, type PeriodGrain } from '../utils/constants';
import { formatIsoWeekRangeLabel, isoYearForCalendarMonth } from '../utils/isoWeek';
import {
  useNodeComments,
  useNodePermissions,
  useShareNode,
  useAddComment,
  useReplyToComment,
  useEditComment,
  useDeleteComment
} from '../api/hooks';
import type { CommentResponse } from '../api/types';

interface OrgUser {
  user_id: number;
  email: string;
  firstname?: string | null;
  surname?: string | null;
}

type CommentNodeType = 'data_card' | 'group_card' | 'status_card';
type CommentCadence = 'monthly' | 'quarterly' | 'weekly' | null;

interface NodeCommentPopupProps {
  nodeId: string;
  nodeName: string;
  scopeId: string;
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  canComment: boolean;
  // Used to label the period honestly: monthly cards attach comments to the
  // month (even on week view), weekly/group cards attach to the viewed week.
  nodeType?: CommentNodeType;
  updateCadence?: CommentCadence;
  // The period currently being viewed on the dashboard. New comments are filed
  // under it (normalized server-side to the card's cadence). Fetching uses
  // grain the same way data does: week grain pins a weekly/group comment to
  // that exact ISO week; month/quarter grain includes every overlapping week
  // so a w39 comment still appears on September / the matching quarter.
  year?: number;
  month?: number;
  week?: number;
  grain?: PeriodGrain;
  /** Render the thread without the popover chrome (card details pane). */
  embedded?: boolean;
  /** When set, the Comment action renders in the panel footer. */
  actionsPortal?: HTMLElement | null;
}

function commentsFollowWeek(nodeType?: CommentNodeType, cadence?: CommentCadence): boolean {
  return nodeType === 'group_card' || cadence === 'weekly';
}

function commentPeriodSubtitle(
  year?: number,
  month?: number,
  week?: number,
  grain?: PeriodGrain,
  nodeType?: CommentNodeType,
  cadence?: CommentCadence
): string {
  if (!year || !month) return '';
  const monthLabel = `${MONTHS[month - 1]} ${year}`;
  if (commentsFollowWeek(nodeType, cadence) && grain === 'week' && week) {
    return ` · ${monthLabel} (${formatIsoWeekRangeLabel(isoYearForCalendarMonth(year, month, week), week)})`;
  }
  if (cadence === 'quarterly' || grain === 'quarter') {
    const quarter = MONTH_TO_FISCAL_QUARTER[month];
    const meta = FISCAL_QUARTERS.find((item) => item.value === quarter);
    return meta ? ` · Q${quarter} ${year} (${meta.rangeLabel})` : ` · ${monthLabel}`;
  }
  return ` · ${monthLabel}`;
}

function commentScopeTooltip(nodeType?: CommentNodeType, cadence?: CommentCadence, grain?: PeriodGrain): string {
  if (commentsFollowWeek(nodeType, cadence)) {
    if (grain === 'week') {
      return 'New comments attach to this week. They also appear on this month and quarter, but not on other weeks.';
    }
    if (grain === 'quarter') {
      return 'Showing comments from every week in this quarter, plus any month-level comments.';
    }
    return 'Showing comments from every week in this month, plus any month-level comments.';
  }
  if (cadence === 'quarterly') {
    return 'This card updates quarterly, so comments attach to the whole quarter, they show whichever month of the quarter you view.';
  }
  return 'This card updates monthly, so comments attach to the month. They show on every week in that month.';
}

export default function NodeCommentPopup({
  nodeId,
  nodeName,
  scopeId,
  open,
  anchorEl,
  onClose,
  canComment,
  nodeType,
  updateCadence,
  year,
  month,
  week,
  grain,
  embedded = false,
  actionsPortal = null
}: NodeCommentPopupProps) {
  const { user } = useAuth();
  const { data: commentsData, isLoading } = useNodeComments(
    nodeId,
    year,
    month,
    grain === 'week' ? week : undefined,
    grain,
    open
  );
  const { data: nodePermissions } = useNodePermissions(scopeId);

  const addCommentMutation = useAddComment();
  const replyMutation = useReplyToComment(nodeId);
  const editMutation = useEditComment(nodeId);
  const deleteMutation = useDeleteComment(nodeId);
  const shareNodeMutation = useShareNode();

  const currentSharesForMentions = useMemo(
    () => nodePermissions?.shared_with?.map((s) => ({ email: s.email, role: s.permission })) || [],
    [nodePermissions]
  );
  const mentionShareFlow = useMentionShareFlow(currentSharesForMentions, ResourcePermissionRole.COMMENT);

  const [newCommentText, setNewCommentText] = useState('');
  const [newCommentMentions, setNewCommentMentions] = useState<MentionData[]>([]);
  const [replyingToId, setReplyingToId] = useState<string | null>(null);
  const [replyText, setReplyText] = useState('');
  const [replyMentions, setReplyMentions] = useState<MentionData[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editText, setEditText] = useState('');
  const [editMentions, setEditMentions] = useState<MentionData[]>([]);

  const { data: orgUsers } = useQuery({
    queryKey: ['account-users-reporting'],
    queryFn: async () => {
      const response = await apiGet<OrgUser[]>('/tenant-management/members', {
        meta: { globalError: false, context: 'reporting-comments' }
      });
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
    enabled: open
  });

  const getUserDisplayName = useCallback(
    (userId: number | null | undefined) => {
      if (!userId) return 'System';
      const orgUser = orgUsers?.find((u) => u.user_id === userId);
      if (!orgUser) return 'Unknown User';
      if (orgUser.firstname && orgUser.surname) return `${orgUser.firstname} ${orgUser.surname}`;
      return orgUser.email;
    },
    [orgUsers]
  );

  const getUserInitials = useCallback(
    (userId: number | null | undefined) => {
      if (!userId) return 'S';
      const orgUser = orgUsers?.find((u) => u.user_id === userId);
      if (!orgUser) return '?';
      if (orgUser.firstname && orgUser.surname) return `${orgUser.firstname[0]}${orgUser.surname[0]}`;
      return orgUser.email[0].toUpperCase();
    },
    [orgUsers]
  );

  const submitComment = useCallback(
    async (mentions: MentionData[]) => {
      try {
        await addCommentMutation.mutateAsync({
          nodeId,
          content: addMentionMarkers(newCommentText.trim(), newCommentMentions),
          mentions: mentions.length > 0 ? mentions : undefined,
          year,
          month,
          week: grain === 'week' ? week : undefined
        });
        setNewCommentText('');
        setNewCommentMentions([]);
      } catch {
        toast.error('Failed to add comment');
      }
    },
    [addCommentMutation, nodeId, newCommentText, newCommentMentions, year, month, week, grain]
  );

  const handleAddComment = () => {
    if (!newCommentText.trim()) return;
    mentionShareFlow.checkAndSubmit(newCommentMentions, submitComment);
  };

  const pendingReplyParentRef = useMemo(() => ({ current: null as string | null }), []);

  const submitReply = useCallback(
    async (mentions: MentionData[]) => {
      const parentId = pendingReplyParentRef.current;
      if (!parentId) return;
      try {
        await replyMutation.mutateAsync({
          commentId: parentId,
          content: addMentionMarkers(replyText.trim(), replyMentions),
          mentions: mentions.length > 0 ? mentions : undefined
        });
        setReplyingToId(null);
        setReplyText('');
        setReplyMentions([]);
      } catch {
        toast.error('Failed to add reply');
      }
    },
    [replyMutation, replyText, replyMentions, pendingReplyParentRef]
  );

  const handleReply = (parentId: string) => {
    if (!replyText.trim()) return;
    pendingReplyParentRef.current = parentId;
    mentionShareFlow.checkAndSubmit(replyMentions, submitReply);
  };

  const pendingEditIdRef = useMemo(() => ({ current: null as string | null }), []);

  const submitEdit = useCallback(async () => {
    const commentId = pendingEditIdRef.current;
    if (!commentId) return;
    try {
      await editMutation.mutateAsync({ commentId, content: addMentionMarkers(editText.trim(), editMentions) });
      setEditingId(null);
      setEditText('');
      setEditMentions([]);
    } catch {
      toast.error('Failed to edit comment');
    }
  }, [editMutation, editText, editMentions, pendingEditIdRef]);

  const handleEdit = (commentId: string) => {
    if (!editText.trim()) return;
    pendingEditIdRef.current = commentId;
    mentionShareFlow.checkAndSubmit(editMentions, submitEdit);
  };

  const handleDelete = async (commentId: string) => {
    try {
      await deleteMutation.mutateAsync(commentId);
      toast.success('Comment deleted');
    } catch {
      toast.error('Failed to delete comment');
    }
  };

  const startEdit = (comment: CommentResponse) => {
    setEditingId(comment.id);
    setEditText(comment.content);
    setEditMentions([]);
    setReplyingToId(null);
  };

  const startReply = (commentId: string) => {
    setReplyingToId(commentId);
    setReplyText('');
    setReplyMentions([]);
    setEditingId(null);
  };

  const isEdited = (comment: CommentResponse) => {
    return comment.modified_at !== comment.created_at;
  };

  const comments = useMemo(() => commentsData?.comments ?? [], [commentsData]);

  const renderComment = (comment: CommentResponse, isReply: boolean = false) => {
    const isAuthor = comment.author_id != null && comment.author_id === user?.user_id;
    const isCurrentlyEditing = editingId === comment.id;

    return (
      <div key={comment.id} className={`group ${isReply ? 'ml-8 border-l-2 border-border-default pl-3' : ''}`}>
        <div className="flex items-start gap-2 py-2">
          {/* Avatar */}
          <div className="flex-shrink-0 w-7 h-7 rounded-full bg-secondary-blue/20 text-secondary-blue flex items-center justify-center text-xs font-medium">
            {getUserInitials(comment.author_id)}
          </div>

          <div className="flex-1 min-w-0">
            {/* Header */}
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-content-default">{getUserDisplayName(comment.author_id)}</span>
              <span className="text-xs text-content-subtle">{formatRelativeTime(comment.created_at)}</span>
              {isEdited(comment) && <span className="text-xs text-content-subtle italic">(edited)</span>}
              {!isReply && comment.period_year == null && (
                <span
                  className="text-[10px] uppercase tracking-wide text-content-subtle border border-border-default rounded px-1"
                  title="Not tied to a specific month. Shows on every period."
                >
                  General
                </span>
              )}
            </div>

            {/* Content or Edit form */}
            {isCurrentlyEditing ? (
              <div className="mt-1 space-y-2">
                <Textarea
                  value={editText}
                  onChange={(e) => setEditText(e.target.value)}
                  mentions={editMentions}
                  onMentionsChange={setEditMentions}
                  rows={2}
                  fullWidth
                  className="text-sm"
                />
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="primary"
                    onClick={() => handleEdit(comment.id)}
                    disabled={!editText.trim() || editMutation.isPending}
                    loading={editMutation.isPending}
                  >
                    Save
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setEditingId(null)}>
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <p className="text-sm text-content-default mt-0.5 whitespace-pre-wrap break-words">
                {renderContentWithMentions(comment.content)}
              </p>
            )}

            {/* Actions */}
            {!isCurrentlyEditing && (
              <div className="flex items-center gap-2 mt-1 opacity-0 group-hover:opacity-100 transition-opacity">
                {canComment && !isReply && (
                  <button
                    onClick={() => startReply(comment.id)}
                    className="text-xs text-content-subtle hover:text-content-default flex items-center gap-1"
                  >
                    <ArrowBendDownRightIcon size={12} />
                    Reply
                  </button>
                )}
                {isAuthor && (
                  <>
                    <button
                      onClick={() => startEdit(comment)}
                      className="text-xs text-content-subtle hover:text-content-default flex items-center gap-1"
                    >
                      <PencilSimpleIcon size={12} />
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(comment.id)}
                      className="text-xs text-content-subtle hover:text-red-500 flex items-center gap-1"
                    >
                      <TrashIcon size={12} />
                      Delete
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Reply input */}
        {replyingToId === comment.id && canComment && (
          <div className="ml-9 mt-1 mb-2 space-y-2">
            <Textarea
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              mentions={replyMentions}
              onMentionsChange={setReplyMentions}
              placeholder="Write a reply... (use @ to mention)"
              rows={2}
              fullWidth
              className="text-sm"
            />
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="primary"
                onClick={() => handleReply(comment.id)}
                disabled={!replyText.trim() || replyMutation.isPending}
                loading={replyMutation.isPending}
              >
                Reply
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setReplyingToId(null)}>
                Cancel
              </Button>
            </div>
          </div>
        )}

        {/* Replies */}
        {comment.replies?.map((reply) => renderComment(reply, true))}
      </div>
    );
  };

  const commentButton = canComment ? (
    <Button
      size="sm"
      variant="primary"
      icon={PaperPlaneTiltIcon}
      onClick={handleAddComment}
      disabled={!newCommentText.trim() || addCommentMutation.isPending}
      loading={addCommentMutation.isPending}
    >
      Comment
    </Button>
  ) : null;
  const pinCommentToPortal = embedded && actionsPortal != null && commentButton != null;

  const thread = (
    <>
      <div className={embedded ? 'flex-1 overflow-y-auto min-h-0' : 'flex-1 overflow-y-auto px-4 py-2 min-h-0'}>
        {isLoading ? (
          <div className="py-6">
            <Loading text="Loading comments..." />
          </div>
        ) : comments.length === 0 ? (
          <div className="py-8 text-center">
            <p className="text-sm text-content-subtle">No comments yet.</p>
            {canComment && <p className="text-xs text-content-subtle mt-1">Be the first to leave a comment.</p>}
          </div>
        ) : (
          <div className="divide-y divide-border-subtle">{comments.map((comment) => renderComment(comment))}</div>
        )}
      </div>
      {canComment && (
        <div className={embedded ? 'pt-3 shrink-0' : 'px-4 py-3 border-t border-border-default'}>
          <Textarea
            value={newCommentText}
            onChange={(e) => setNewCommentText(e.target.value)}
            mentions={newCommentMentions}
            onMentionsChange={setNewCommentMentions}
            placeholder="Add a comment... (use @ to mention)"
            rows={2}
            fullWidth
            className="text-sm"
          />
          {!pinCommentToPortal && <div className="flex justify-end mt-2">{commentButton}</div>}
        </div>
      )}
    </>
  );

  const shareModal = (
    <ShareModal
      open={mentionShareFlow.showShareModal}
      onClose={mentionShareFlow.handleShareModalClose}
      onApplySuccess={mentionShareFlow.handleShareComplete}
      onAddPeople={async (emails, permission, message, groupIds) => {
        await shareNodeMutation.mutateAsync({
          nodeId: scopeId,
          data: {
            emails: emails.length > 0 ? emails : undefined,
            permission,
            message,
            group_ids: groupIds
          }
        });
      }}
      initialEmails={mentionShareFlow.shareModalEmails}
      title={nodeName}
      shareType="dashboard"
      defaultPermission={ResourcePermissionRole.COMMENT}
      user_email={user?.email}
      mentionPrompt={
        mentionShareFlow.mentionsNeedingAccess.length > 0
          ? {
              mentionsNeedingAccess: mentionShareFlow.mentionsNeedingAccess,
              recommendedPermissionHint: 'Comment access is required to participate in discussions.'
            }
          : undefined
      }
    />
  );

  if (embedded) {
    return (
      <>
        <div className="flex flex-col h-full min-h-0">{thread}</div>
        {pinCommentToPortal && actionsPortal ? createPortal(commentButton, actionsPortal) : null}
        {shareModal}
      </>
    );
  }

  return (
    <>
      <Popover
        open={open && !mentionShareFlow.showShareModal}
        anchorEl={anchorEl}
        onClose={onClose}
        placement="bottom-end"
        className="w-[420px] max-h-[500px] flex flex-col bg-surface border border-border-default rounded-lg shadow-xl z-50 overflow-visible"
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-border-default">
          <div>
            <h3 className="text-sm font-semibold text-content-default">Comments</h3>
            <p className="text-xs text-content-subtle flex items-center gap-1 min-w-0 max-w-[300px]">
              <span className="truncate">
                {nodeName}
                {commentPeriodSubtitle(year, month, week, grain, nodeType, updateCadence)}
              </span>
              <span className="relative group/hint inline-flex flex-shrink-0">
                <span
                  className="text-content-subtle hover:text-content-default cursor-help p-0.5"
                  aria-label="When comments are shown"
                >
                  <InfoIcon size={12} weight="bold" />
                </span>
                <span className="pointer-events-none absolute left-1/2 top-full z-[1000] mt-1.5 w-56 -translate-x-1/2 rounded bg-gray-700 px-2 py-1 text-xs text-white text-left leading-snug opacity-0 transition-opacity group-hover/hint:opacity-100 group-hover/hint:delay-150 before:absolute before:left-1/2 before:top-[-5px] before:-translate-x-1/2 before:border-x-[5px] before:border-x-transparent before:border-b-[5px] before:border-b-gray-700">
                  {commentScopeTooltip(nodeType, updateCadence, grain)}
                </span>
              </span>
            </p>
          </div>
          <button onClick={onClose} className="text-content-subtle hover:text-content-default p-1 rounded transition">
            <XIcon size={16} />
          </button>
        </div>
        {thread}
      </Popover>
      {shareModal}
    </>
  );
}
